import { useContext } from 'react'
import { ToastContext } from '../context/ToastContext'
import { ToastVariant, ToastPosition } from '../types/toast.types'
import { ColorScheme } from '@/types/Content.types'

import { IconProps as SvgIconProps } from '@/components/atoms/Icon'

/**
 * Options for showing a toast
 */
interface ToastOptions {
  variant?: ToastVariant
  duration?: number
  position?: ToastPosition
  colorScheme?: ColorScheme
  showCloseButton?: boolean
  Icon?: React.ComponentType<SvgIconProps> | string
  persistent?: boolean
  hideIcon?: boolean
}

/**
 * Hook to use the Toast context with a simplified API
 * @returns Simplified methods for managing toasts
 */
export const useToast = () => {
  const context = useContext(ToastContext)

  if (!context) {
    throw new Error('useToast must be used inside a ToastProvider')
  }

  /**
   * Show a new toast with message and options
   * @param message - The message to display
   * @param options - Toast configuration options
   * @returns Unique ID of the created toast
   */
  const show = (message: React.ReactNode, options: ToastOptions = {}) => {
    const toastConfig = {
      message,
      variant: options.variant || 'info',
      duration: options.duration,
      position: options.position,
      colorScheme: options.colorScheme as ColorScheme,
      showCloseButton:
        options.showCloseButton !== undefined ? options.showCloseButton : true,
      Icon: options.Icon,
      persistent: options.persistent,
      hideIcon: options.hideIcon,
    }

    return context.showToast(toastConfig)
  }

  // Support functions for common variants
  const success = (
    message: React.ReactNode,
    options: Omit<ToastOptions, 'variant'> = {}
  ) => {
    return show(message, { ...options, variant: 'success' })
  }

  const error = (
    message: React.ReactNode,
    options: Omit<ToastOptions, 'variant'> = {}
  ) => {
    return show(message, { ...options, variant: 'error' })
  }

  const warning = (
    message: React.ReactNode,
    options: Omit<ToastOptions, 'variant'> = {}
  ) => {
    return show(message, { ...options, variant: 'warning' })
  }

  const info = (
    message: React.ReactNode,
    options: Omit<ToastOptions, 'variant'> = {}
  ) => {
    return show(message, { ...options, variant: 'info' })
  }

  /**
   * Remove a specific toast
   * @param id - ID of the toast to remove
   */
  const remove = (id: string) => {
    context.removeToast(id)
  }

  /**
   * Remove all currently visible toasts
   */
  const removeAll = () => {
    // Use the context method to remove all toasts
    context.clearToasts()
  }

  return {
    show,
    success,
    error,
    warning,
    info,
    remove,
    removeAll,
    toasts: context.toasts,
  }
}
